import type { Method } from 'axios'

import type { UseMutationOptions } from '@tanstack/react-query'

import { useMutationApi, useQueryApi } from 'utils/api'
import { handleCommonErrors } from 'utils/api'
import { CustomError } from 'utils/customError'

import type { CommonListResponse, CommonResponse } from 'typings/api'
import type {
  AksiNyataCreateBody,
  AksiNyataOption,
  AksiNyataSession,
  MilUseQueryError,
  TAPIGetAksiNyataRevisionResponse,
  TopicDetails,
} from 'typings/microlearning'

export class JenjangUnmatched extends CustomError {
  constructor(apiPath: string, originalError: any) {
    const title = 'Jenjang tidak sesuai'
    const description =
      'Topik yang anda akses tidak sesuai dengan jenjang akun anda.'
    super({ title, description, apiPath, originalError })
  }
}

export function useGetTopicDetail(topicId: string) {
  return useQueryApi<CommonResponse<TopicDetails>>(
    `/microlearnings/topics/${topicId}`,
    {
      queryConfig: {
        enabled: !!topicId,
      },
      errorHandlerFn: (status, path, error, errorResponse) => {
        if (
          status === 400 &&
          !!errorResponse.data?.errors[0]?.code &&
          errorResponse.data?.errors[0]?.code === '08-118'
        ) {
          throw new JenjangUnmatched(path, error)
        } else {
          handleCommonErrors(status ?? 500, path, error)
        }
      },
    }
  )
}

export function useGetTopicAksiNyataOptions(topicId: string, enabled: boolean) {
  return useQueryApi<CommonListResponse<AksiNyataOption>, MilUseQueryError>(
    `/microlearnings/topics/${topicId}/aksi-nyata-options`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
      },
    }
  )
}

export function useGetTopicAksiNyataSession(
  topicId: string,
  enabled: boolean,
  data = {}
) {
  return useQueryApi<CommonResponse<AksiNyataSession>, MilUseQueryError>(
    `/microlearnings/topics/${topicId}/aksi-nyata`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
        useErrorBoundary: false,
      },
      axiosConfig: {
        data,
      },
    }
  )
}

export function usePostAksiNyata(
  topicId: string,
  method: Method,
  opts?: UseMutationOptions<
    CommonResponse<AksiNyataSession>,
    Error,
    AksiNyataCreateBody
  >
) {
  return useMutationApi<
    CommonResponse<AksiNyataSession>,
    Error,
    AksiNyataCreateBody
  >(`/microlearnings/topics/${topicId}/aksi-nyata`, {
    mutationConfig: opts,
    axiosConfig: {
      method,
    },
  })
}

export interface RelevanceTopics {
  topics: {
    id: string
    name: string
  }[]
}

export function useGetRelevanceTopic(topicId: string) {
  return useQueryApi<CommonResponse<RelevanceTopics>>(
    `/microlearnings/topics/${topicId}/relevances`,
    {
      queryConfig: {
        useErrorBoundary: false,
      },
    }
  )
}

export function useGetAksiNyataRevision(
  topicId: string | number,
  enabled: boolean
) {
  return useQueryApi<CommonResponse<TAPIGetAksiNyataRevisionResponse>>(
    `/microlearnings/topics/${topicId}/aksi-nyata/revision`,
    {
      queryConfig: {
        enabled: !!topicId && enabled,
        useErrorBoundary: false,
      },
    }
  )
}
